<script lang="ts">
	import { format } from 'svelte-i18n';

	import {
		get_gen_2_component_channel_structure,
		get_gen_2_component_from_category,
		real_id,
	} from '../../helpers/interface_helpers';
	import { capFirstChar, convert } from '../../helpers/utilities';

		import { getDeviceStatus, getDeviceSettings, readDeviceStatus } from 'src/stores/device-runtime/status-selectors';

	// import PowerDataByPhase from './PowerDataByPhase.svelte';
	// import PowerDataTotal from './PowerDataTotal.svelte';
	// import PowerDataN from './PowerDataN.svelte';

	import { ParameterCardV2, Row, Col, Text } from '@Shelly/shelly-design-system';

	import { useNavigateToHistoryStatisticsTab } from '../Sidepanel/useNavigateToHistoryStatisticsTab';

	$: statusStore = getDeviceStatus(device.id);
	export let device, status;

	const { navigateToHistoryStatisticsTab } = useNavigateToHistoryStatisticsTab();

	const phases = ['a', 'b', 'c'];

	let phaseCardSize: 'xs' | 's' = 's';
	$: phaseCardSize = phases.length >= 3 ? 'xs' : 's';

	const userCalibrated =
		$statusStore?.[get_gen_2_component_channel_structure(device)]
			?.user_calibrated_phase;

	type EmeterData = {
		a_act_power: number;
		b_act_power: number;
		c_act_power: number;
		a_aprt_power: number;
		b_aprt_power: number;
		c_aprt_power: number;
		a_current: number;
		b_current: number;
		c_current: number;
		a_voltage: number;
		b_voltage: number;
		c_voltage: number;
		a_pf: number;
		b_pf: number;
		c_pf: number;
		a_total_act_energy: number;
		a_total_act_ret_energy: number;
		b_total_act_energy: number;
		b_total_act_ret_energy: number;
		c_total_act_energy: number;
		c_total_act_ret_energy: number;
		user_calibrated_phase: string[];
		total_act_power: number;
	};

	type EmeterTotalData = {
		total_act: number;
		total_act_ret: number;
	};

	let emeterData: EmeterData;
	let emeterTotalData: EmeterTotalData;
	$: emeterTotalData = status?.[`${get_gen_2_component_from_category(device)}data:${device?.channel}`];
	$: emeterData = {
		...status?.[get_gen_2_component_channel_structure(device)],
		...emeterTotalData,
	};
	$: if (emeterData?.user_calibrated_phase?.includes('n') && 'n_current' in emeterData) {
		n_phase = emeterData.n_current;
		total_n = [{ title: $format('current_n'), value: n_phase, units: 'A' }];
	}

	let n_phase,
		total_n,
		wUnit = 'W',
		whUnit = 'Wh',
		vaUnit = 'VA',
		aUnit = 'A',
		vUnit = 'V';

	const totalsCalc = (items) => {
		if (Object.keys(items).length > 0) {
			let total_arr = [],
				power = 0,
				aprt_power = 0,
				current_power = 0;

			power = Object.keys(items)
				.filter((k) => k.indexOf('act_power') !== -1)
				.map((k) => items[k])
				.reduce((a, b) => a + b);
			aprt_power += Object.keys(items)
				.filter((k) => k.indexOf('aprt_power') !== -1)
				.map((k) => items[k])
				.reduce((a, b) => a + b);
			current_power += Object.keys(items)
				.filter((k) => k.indexOf('current') !== -1)
				.map((k) => items[k])
				.reduce((a, b) => a + b);
			total_arr = [
				{
					title: $format('em_power', { default: 'power' }),
					value: power,
					units: wUnit,
				},
				{
					title: $format('em_aparent_power', { default: 'apparent power' }),
					value: aprt_power,
					units: vaUnit,
				},
				{
					title: $format('em_current', { default: 'current power' }),
					value: current_power,
					units: aUnit,
				},
			];

			return total_arr;
		}
	};

	const formatTotals = (em) => {
		return [
			{
				title: $format('power', { default: ' power' }),
				value: em.total_act_power,
				units: 'W',
			},
			{
				title: $format('aprt_power', { default: 'apparent power' }),
				value: em.total_aprt_power,
				units: 'VA',
			},
			{
				title: $format('em_current', { default: 'current power' }),
				value: em.total_current,
				units: aUnit,
			},
		];
	};
</script>

<Text styleType="h2" class="mb-s {phases.length >= 3 ? ' label-large-styles' : 'text-capitalize'}">
	{$format('title_total', { default: 'total' })}
</Text>

{#if emeterData}
	<Row class="mb-s">
		{#each typeof emeterData?.total_act_power !== 'undefined' ? formatTotals(emeterData) : totalsCalc(emeterData) as totalItem, i}
			<Col>
				<ParameterCardV2
					title={convert(totalItem.value, totalItem.units)}
					description={capFirstChar(totalItem.title)}
					size="s"
				/>
			</Col>
		{/each}
	</Row>
{/if}

{#if emeterTotalData}
	<Row class="mb-m">
		<Col>
			<ParameterCardV2
				title={convert(emeterTotalData.total_act, whUnit)}
				description={$format('em_overall')}
				size="s"
			/>
		</Col>
	</Row>
{/if}

<!-- N channel -->
{#if typeof total_n !== 'undefined'}
	<Text styleType="h2" class="mb-s text-capitalize"
		>{capFirstChar($format('label_power_n_phase', { default: 'Neutral' }))}</Text
	>

	<Row class="mb-m">
		{#each total_n as totalItem}
			<Col>
				<ParameterCardV2
					title={convert(totalItem.value, totalItem.units)}
					description={totalItem.title}
					size="s"
				/>
			</Col>
		{/each}
	</Row>
{/if}

<Row>
	{#each phases as phase, i}
		<Col xs="4">
			<Text
				styleType="h2"
				class="mb-s text-nowrap {phases.length >= 3 ? ' label-large-styles' : 'text-capitalize'}"
				>{$format(`label_phase_${phase}`, {
					default: `phase ${phase}`,
				})}
				{#if userCalibrated?.includes(phase)}
					<Text element="span" styleType="controls-small" class="text-primary">
						<strong>{' *'}</strong>
					</Text>
				{/if}
			</Text>
			<ParameterCardV2
				title={convert(emeterData?.[phase + '_act_power'], wUnit)}
				description={capFirstChar($format('em_power', { default: 'power' }))}
				size={phaseCardSize}
				class="mb-s"
			/>

			{#if typeof emeterData?.[phase + '_total_act_ret_energy'] !== 'undefined'}
				<ParameterCardV2
					title={convert(emeterData?.[phase + '_total_act_ret_energy'], whUnit)}
					description={capFirstChar(
						$format('label_total_returned', { default: 'total returned' }),
					)}
					size={phaseCardSize}
					class="mb-s"
					on:click={navigateToHistoryStatisticsTab}
					isFocusable
				/>
			{/if}

			<ParameterCardV2
				title={convert(emeterData?.[phase + '_total_act_energy'], whUnit)}
				description={capFirstChar($format('em_overall'))}
				size={phaseCardSize}
				class="mb-s"
			/>

			<ParameterCardV2
				title={convert(emeterData?.[phase + '_aprt_power'], vaUnit)}
				description={capFirstChar($format('em_aparent_power', { default: 'apparent power' }))}
				size={phaseCardSize}
				class="mb-s"
			/>

			{#if typeof emeterData?.[phase + '_current'] !== 'undefined'}
				<ParameterCardV2
					title={convert(emeterData?.[phase + '_current'], aUnit)}
					description={capFirstChar($format('em_current', { default: 'current' }))}
					size={phaseCardSize}
					class="mb-s"
				/>
			{/if}
			<ParameterCardV2
				title={convert(emeterData?.[phase + '_voltage'], vUnit)}
				description={capFirstChar($format('em_voltage', { default: 'voltage' }))}
				size={phaseCardSize}
				class="mb-s"
			/>

			<ParameterCardV2
				title={emeterData?.[phase + '_pf'] === 0 ? 'N/A' : emeterData?.[phase + '_pf']}
				description={capFirstChar($format('em_power_factor', { default: 'power factor' }))}
				size={phaseCardSize}
				class="mb-s"
			/>
		</Col>
	{/each}
</Row>
